/**
 * LaunchSafetyBadge — read-only pre-launch safety indicator.
 *
 * Consumes GET /api/admin/launch-reset/status (admin-only, non-destructive)
 * and renders a single pill:
 *   - GREEN  "RESET DISABLED" — normal production state (ALLOW_LAUNCH_RESET≠true)
 *   - RED    "RESET ARMED"    — only when ALLOW_LAUNCH_RESET=true
 *
 * It is purely informational: NO reset button, NO action controls, NO link
 * to the destructive endpoint, and it surfaces none of the reset internals
 * (sentinel token, backup paths, DB credentials). The status endpoint itself
 * only ever returns safe booleans/labels.
 */
import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "../../lib/api";

export const LaunchSafetyBadge = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/launch-reset/status")
      .then((res) => {
        if (alive) setStatus(res.data);
      })
      .catch(() => {
        // Endpoint unavailable or non-admin — render nothing rather than
        // surfacing an error. This is a passive safety indicator only.
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!status) return null;

  const armed = status.destructive_endpoint_state === "armed";

  return (
    <div
      data-testid="launch-safety-badge"
      data-state={armed ? "armed" : "disabled"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide ${
        armed
          ? "bg-[#EF4444] text-white animate-pulse"
          : "bg-[#10B981] text-white"
      }`}
      title={
        armed
          ? "Launch reset is ARMED in this environment (ALLOW_LAUNCH_RESET=true). Disable before go-live."
          : "Launch reset is disabled — the destructive endpoint is unreachable."
      }
    >
      {armed ? (
        <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.5} />
      ) : (
        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
      )}
      <span>{armed ? "Reset Armed" : "Reset Disabled"}</span>
    </div>
  );
};
